/**
 * Dashboard List Page
 * Displays all available dashboards
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, LayoutDashboard, Clock, Settings, Trash2 } from 'lucide-react';
import { Layout } from '../components/layout';
import { dashboardsApi } from '../api/client';
import { Dashboard } from '../types';
import { useAuth } from '../contexts';

export function DashboardListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const { isAdmin } = useAuth();

  const {
    data: dashboards,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dashboards'],
    queryFn: dashboardsApi.list,
  });

  const filteredDashboards = dashboards?.filter(
    (d) =>
      d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Layout
      title="Dashboards"
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Dashboards</h1>
            <p className="text-gray-500 mt-1">
              Manage and view your visualization dashboards
            </p>
          </div>
          {isAdmin && (
            <Link
              to="/dashboards/new"
              className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Dashboard
            </Link>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
              >
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                <div className="h-4 bg-gray-100 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600">
              Failed to load dashboards. Please try again.
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredDashboards?.length === 0 && (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <LayoutDashboard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No dashboards found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Get started by creating your first dashboard'}
            </p>
            {!searchQuery && isAdmin && (
              <Link
                to="/dashboards/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf]"
              >
                <Plus className="w-5 h-5" />
                Create Dashboard
              </Link>
            )}
          </div>
        )}

        {/* Dashboard Grid */}
        {!isLoading && !error && filteredDashboards && filteredDashboards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDashboards.map((dashboard) => (
              <DashboardCard key={dashboard.id} dashboard={dashboard} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function DashboardCard({ dashboard }: { dashboard: Dashboard }) {
  return (
    <Link
      to={`/dashboards/${dashboard.id}`}
      className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all"
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <LayoutDashboard className="w-5 h-5 text-[#45bdd6]" />
            </div>
            <h3 className="font-medium text-gray-900">{dashboard.name}</h3>
          </div>
          {dashboard.auto_refresh && (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
              <Clock className="w-3 h-3" />
              Auto
            </span>
          )}
        </div>

        {dashboard.description && (
          <p className="text-sm text-gray-500 mb-4 line-clamp-2">
            {dashboard.description}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>{dashboard.widget_count ?? dashboard.widgets?.length ?? 0} widgets</span>
          <span>Updated {new Date(dashboard.updated_at).toLocaleDateString()}</span>
        </div>
      </div>
    </Link>
  );
}
